import { StorageSerializers, useStorage } from '@vueuse/core';

export const EVENT_TYPE_PICKER_EXPANDED_KEY = 'kp-event-type-picker-expanded';
export const MODULE_PREFERENCES_KEY = 'kp-dashboard-module-preferences';

/**
 * Key of the vuex-persistedstate snapshot (navigation selection, and formerly these preferences).
 * @param {string} hostname
 * @returns {string}
 */
export function legacyPersistedStateKey(hostname) {
  return `dashboardNavigation_${hostname}`;
}

/**
 * Copies, once, the preferences formerly persisted by vuex-persistedstate into their own keys,
 * so users keep them after the move. A new key that already exists wins; an unreadable legacy
 * value is ignored. Temporary: remove together with vuex-persistedstate (Pinia migration).
 * @param {Storage} storage
 * @param {string} hostname
 * @returns {void}
 */
export function migrateLegacyPreferences(storage, hostname) {
  const missingPickerKey = storage.getItem(EVENT_TYPE_PICKER_EXPANDED_KEY) === null;
  const missingModuleKey = storage.getItem(MODULE_PREFERENCES_KEY) === null;
  if (!missingPickerKey && !missingModuleKey) {
    return;
  }

  try {
    const legacy = JSON.parse(storage.getItem(legacyPersistedStateKey(hostname)) ?? 'null');

    const eventTypePickerExpanded = legacy?.events?.isEventTypePickerExpand;
    if (missingPickerKey && typeof eventTypePickerExpanded === 'boolean') {
      storage.setItem(
        EVENT_TYPE_PICKER_EXPANDED_KEY,
        StorageSerializers.boolean.write(eventTypePickerExpanded),
      );
    }

    const modulePreferences = legacy?.module?.persistedData;
    if (missingModuleKey && modulePreferences && typeof modulePreferences === 'object') {
      storage.setItem(MODULE_PREFERENCES_KEY, StorageSerializers.object.write(modulePreferences));
    }
  } catch {
    // Unreadable legacy value: the defaults apply.
  }
}

// Runs when this module is first evaluated: `store/index.js` imports it, so this happens before
// vuex-persistedstate rewrites the legacy key (without these preferences) on the first commit.
// The refs must be created after it, or their written defaults would block the takeover.
migrateLegacyPreferences(window.localStorage, window.location.hostname);

// One state per tab, as with Vuex: following other tabs would let a sign-out `localStorage.clear()`
// there reset these refs to their defaults and write them back.
const eventTypePickerExpanded = useStorage(EVENT_TYPE_PICKER_EXPANDED_KEY, false, undefined, {
  listenToStorageChanges: false,
});
// Always replaced as a whole (see useModule.persistData), never mutated in place.
const modulePreferences = useStorage(MODULE_PREFERENCES_KEY, {}, undefined, {
  shallow: true,
  deep: false,
  listenToStorageChanges: false,
});

/**
 * Writes the preferences back after a full `localStorage.clear()` (sign-out): they belong to the
 * browser, not to the session, as when vuex-persistedstate rewrote them on the LOGOUT commit.
 * @returns {void}
 */
export function rewritePersistedPreferences() {
  const { boolean, object } = StorageSerializers;
  const storage = window.localStorage;
  storage.setItem(EVENT_TYPE_PICKER_EXPANDED_KEY, boolean.write(eventTypePickerExpanded.value));
  storage.setItem(MODULE_PREFERENCES_KEY, object.write(modulePreferences.value));
}

/**
 * User display preferences persisted in localStorage, shared by every component of the tab.
 * @returns {{
 *   eventTypePickerExpanded: import('vue').Ref<boolean>,
 *   modulePreferences: import('vue').Ref<object>,
 * }}
 */
export default function usePersistedPreferences() {
  return {
    eventTypePickerExpanded,
    modulePreferences,
  };
}
